import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, BarChart3, BookOpen, Briefcase, Building2, CalendarCheck, CheckCircle2,
  GraduationCap, Handshake, IndianRupee, Kanban, Search, ShieldCheck, Sparkles,
  UserCheck, Users, Wallet, Cloud, CreditCard, Video, Calendar, MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api, { inr } from "@/lib/api";

function HeroDashboard() {
  const tiles = [
    { label: "ATS Pipeline", icon: Briefcase },
    { label: "AI Matching", icon: Users },
    { label: "Video Interviews", icon: Video },
    { label: "HRMS & Leave", icon: UserCheck },
    { label: "Attendance", icon: CalendarCheck },
    { label: "Payroll", icon: IndianRupee },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      className="relative animate-float-slow"
      data-testid="hero-dashboard"
    >
      <div className="navy-surface border border-white/10 rounded-2xl p-5 shadow-2xl shadow-black/40 backdrop-blur">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#F5A623]" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <p className="text-xs text-white/50 font-medium">Job Spring — Workforce OS</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {tiles.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.12 }}
              className="bg-white/5 border border-white/10 rounded-xl p-3"
            >
              <s.icon className="w-4 h-4 text-[#F5A623] mb-2" />
              <p className="text-white font-heading font-bold text-sm leading-tight">{s.label}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#2563EB]" />
            <span className="text-xs text-white/60">Hiring funnel analytics</span>
          </div>
          <div className="flex gap-1 items-end h-8">
            {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
              <motion.span
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ delay: 0.9 + i * 0.08, duration: 0.4 }}
                className="w-2 rounded-sm bg-gradient-to-t from-[#2563EB] to-[#F5A623]"
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const LIFECYCLE = [
  { icon: Search, title: "Discover", desc: "AI-matched jobs and talent discovery" },
  { icon: Briefcase, title: "Hire", desc: "ATS, interviews, offers, BGV" },
  { icon: UserCheck, title: "Onboard", desc: "Documents, checklists, day-one ready" },
  { icon: CalendarCheck, title: "Manage", desc: "Attendance, leave, assets, performance" },
  { icon: IndianRupee, title: "Pay", desc: "Payroll, reimbursements, compliance" },
  { icon: GraduationCap, title: "Grow", desc: "Learning marketplace & certificates" },
];

const AUDIENCES = [
  { icon: Building2, title: "For Employers", desc: "Post jobs, run ATS, search talent, manage HRMS and payroll — all from one subscription.", cta: "Start Hiring", to: "/register?role=employer_owner", testId: "audience-employers" },
  { icon: Users, title: "For Candidates", desc: "Build your CV, get AI job matches, ace interviews with AI prep and track every application.", cta: "Find Jobs", to: "/jobs", testId: "audience-candidates" },
  { icon: CalendarCheck, title: "For Employees", desc: "Attendance, leave, payslips, documents, learning and performance in one workspace.", cta: "Explore", to: "/register?role=candidate", testId: "audience-employees" },
  { icon: Handshake, title: "For Hiring Partners", desc: "Access verified openings, submit candidates, track placements and earn payouts.", cta: "Become a Partner", to: "/register?role=franchise_owner", testId: "audience-partners" },
  { icon: GraduationCap, title: "For Training Institutes", desc: "Publish courses to a career-focused audience, deliver learning and receive payouts.", cta: "Join as Institute", to: "/register?role=institute_owner", testId: "audience-institutes" },
];

const FEATURES = [
  { icon: Briefcase, title: "Recruitment", desc: "Multi-step job posting, admin-governed quality review and smart distribution." },
  { icon: Search, title: "Talent Search", desc: "Filter or ask AI in plain language — with consent-based contact privacy." },
  { icon: Kanban, title: "ATS Pipeline", desc: "Kanban stages from applied to joined with notes, interviews and feedback." },
  { icon: Users, title: "HRMS", desc: "Directory, attendance, leave, holidays, shifts and assets." },
  { icon: IndianRupee, title: "Payroll", desc: "Configurable country-specific payroll engines for India, UAE and beyond." },
  { icon: BookOpen, title: "Learning", desc: "Institute marketplace with verified certificates and skill-gap recommendations." },
  { icon: Sparkles, title: "AI Engine", desc: "JD generation, match scores, CV scoring and interview preparation." },
  { icon: BarChart3, title: "Analytics", desc: "Time-to-hire, funnel, revenue and workforce insights for every role." },
];

const INTEGRATIONS = [
  { icon: Cloud, name: "AWS" },
  { icon: CreditCard, name: "PayU" },
  { icon: Video, name: "Zoom" },
  { icon: Calendar, name: "Google Calendar" },
  { icon: MessageSquare, name: "WhatsApp" },
  { icon: ShieldCheck, name: "BGV Partners" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [landing, setLanding] = useState(null);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    api.get("/public/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
    api.get("/public/landing").then(({ data }) => setLanding(data)).catch(() => {});
    api.get("/public/blogs").then(({ data }) => setBlogs(data.blogs)).catch(() => {});
  }, []);

  const activeBanner = (landing?.banners || []).find((b) => b.active && b.text);
  const testimonials = (landing?.testimonials || []).filter((t) => t.quote && t.name);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="navy-bg noise-overlay pt-32 pb-24 lg:pt-40 lg:pb-32 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#2563EB]/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-[#F5A623]/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-[#F5A623] border border-[#F5A623]/30 rounded-full px-4 py-1.5 mb-8">
              <Sparkles className="w-3.5 h-3.5" /> One Platform. Complete Workforce Management.
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-white tracking-tight leading-[1.08]">
              {landing?.hero_headline || "The Future of Hiring & Workforce Management Is Here."}
            </h1>
            <p className="text-base md:text-lg text-white/70 mt-6 max-w-xl leading-relaxed">
              {landing?.hero_subheading ||
                "From finding your next employee to managing their complete lifecycle, Job Spring brings recruitment, HRMS, payroll, learning and workforce management into one intelligent platform."}
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Button
                size="lg"
                onClick={() => navigate("/register?role=employer_owner")}
                className="rounded-full bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-bold px-8"
                data-testid="hero-start-hiring-btn"
              >
                Start Hiring <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/jobs")}
                className="rounded-full border-white/25 text-white hover:bg-white/10 hover:text-white px-8"
                data-testid="hero-find-jobs-btn"
              >
                Find Jobs
              </Button>
            </div>
            <div className="flex flex-wrap gap-6 mt-8 text-sm">
              <Link to="/register?role=franchise_owner" className="text-white/60 hover:text-[#F5A623] transition-colors font-medium" data-testid="hero-partner-link">
                Become a Hiring Partner →
              </Link>
              <Link to="/register?role=institute_owner" className="text-white/60 hover:text-[#F5A623] transition-colors font-medium" data-testid="hero-institute-link">
                Join as Training Institute →
              </Link>
            </div>
          </motion.div>
          <HeroDashboard />
        </div>
      </section>

      {/* PROMO BANNER */}
      {activeBanner && (
        <div className="bg-[#F5A623] text-[#0B1B3D] text-center text-sm font-semibold py-3 px-4" data-testid="promo-banner">
          {activeBanner.link ? (
            <Link to={activeBanner.link} className="hover:underline">{activeBanner.text} →</Link>
          ) : activeBanner.text}
        </div>
      )}

      {/* ECOSYSTEM */}
      <section className="bg-white border-b border-border/60 py-10">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground text-center mb-6">
            Integration-ready ecosystem
          </p>
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-4">
            {INTEGRATIONS.map((it) => (
              <div key={it.name} className="flex items-center gap-2 text-slate-400" data-testid={`eco-${it.name.toLowerCase().replace(/\s/g, "-")}`}>
                <it.icon className="w-5 h-5" />
                <span className="font-heading font-semibold text-sm">{it.name}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            {landing?.ecosystem_note || "Genuine customer logos and statistics are published from the Admin CMS once verified."}
          </p>
        </div>
      </section>

      {/* LIFECYCLE */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#2563EB] mb-4">Entire Employee Lifecycle</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight text-[#0B1B3D]">
              One Platform. Hire. Onboard. Manage. Pay. Learn. Grow.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {LIFECYCLE.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white border border-border/60 rounded-2xl p-7 card-hover"
                data-testid={`lifecycle-${s.title.toLowerCase()}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-5">
                  <s.icon className="w-5 h-5 text-[#2563EB]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#0B1B3D]">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="navy-bg noise-overlay py-24 lg:py-32" id="partners">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#F5A623] mb-4">Built for every player</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight text-white max-w-2xl">
            A workforce ecosystem, not just a job portal.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {AUDIENCES.map((a, i) => (
              <motion.div
                key={a.title}
                id={a.title === "For Training Institutes" ? "institutes" : undefined}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="navy-surface border border-white/10 rounded-2xl p-7 hover:border-[#F5A623]/40 transition-colors"
                data-testid={a.testId}
              >
                <a.icon className="w-6 h-6 text-[#F5A623] mb-5" />
                <h3 className="font-heading font-semibold text-lg text-white">{a.title}</h3>
                <p className="text-sm text-white/60 mt-2 leading-relaxed min-h-[60px]">{a.desc}</p>
                <Link
                  to={a.to}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#F5A623] mt-5 hover:gap-2.5 transition-all"
                >
                  {a.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#2563EB] mb-4">Platform Capabilities</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight text-[#0B1B3D]">
              Everything from first application to final settlement.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className={`rounded-2xl p-7 border card-hover ${
                  i === 0 || i === 6
                    ? "sm:col-span-2 bg-[#0B1B3D] text-white border-transparent"
                    : "bg-white border-border/60"
                }`}
                data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <f.icon className={`w-6 h-6 mb-5 ${i === 0 || i === 6 ? "text-[#F5A623]" : "text-[#2563EB]"}`} />
                <h3 className={`font-heading font-semibold text-lg ${i === 0 || i === 6 ? "text-white" : "text-[#0B1B3D]"}`}>{f.title}</h3>
                <p className={`text-sm mt-2 leading-relaxed ${i === 0 || i === 6 ? "text-white/60" : "text-muted-foreground"}`}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-border/60 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#2563EB] mb-4">How It Works</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight text-[#0B1B3D] max-w-2xl">
            From subscription to successful joining — governed end to end.
          </h2>
          <div className="grid md:grid-cols-4 gap-8 mt-14">
            {[
              { n: "01", t: "Subscribe & Post", d: "Choose a plan, post jobs with a guided wizard, and pick how you want to hire — your team, our HR consultants, or hiring partners." },
              { n: "02", t: "Quality Review", d: "Every job passes admin review — approve, reject, request correction or hold — before it reaches candidates and partners." },
              { n: "03", t: "Screen & Select", d: "Run your ATS pipeline with AI match scores, structured interviews and consent-based candidate privacy." },
              { n: "04", t: "Hire & Manage", d: "On join, candidates become employees automatically — onboarding, payroll and learning continue in the same platform." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`how-${s.n}`}
              >
                <p className="font-heading font-extrabold text-5xl text-[#2563EB]/15">{s.n}</p>
                <h3 className="font-heading font-semibold text-lg text-[#0B1B3D] mt-3">{s.t}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="py-24 lg:py-32" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-xs font-bold uppercase tracking-widest text-[#2563EB] mb-3">Testimonials</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#0B1B3D] mb-14">What our customers say</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-white border border-border/60 rounded-2xl p-7 card-hover" data-testid={`testimonial-${i}`}>
                  <p className="text-sm text-slate-600 leading-relaxed">“{t.quote}”</p>
                  <div className="mt-6 pt-4 border-t border-border/50">
                    <p className="font-heading font-semibold text-sm text-[#0B1B3D]">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRICING */}
      <section className="py-24 lg:py-32" id="pricing">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#2563EB] mb-4">Pricing</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight text-[#0B1B3D]">
              Plans that scale with your hiring.
            </h2>
            <p className="text-sm text-muted-foreground mt-4">
              Transparent pricing configured by the platform team. GST applicable. Upgrade, downgrade or cancel anytime.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {plans.map((p) => (
              <div
                key={p.id}
                className={`rounded-2xl p-7 bg-white border card-hover flex flex-col ${
                  p.highlight ? "glow-border border-[#F5A623]" : "border-border/60"
                }`}
                data-testid={`plan-card-${p.id}`}
              >
                {p.highlight && (
                  <span className="text-[10px] font-bold tracking-widest uppercase text-[#0B1B3D] bg-[#F5A623] rounded-full px-3 py-1 self-start mb-4">
                    Most Popular
                  </span>
                )}
                <h3 className="font-heading font-bold text-xl text-[#0B1B3D]">{p.name}</h3>
                <p className="mt-4">
                  {p.custom ? (
                    <span className="font-heading font-extrabold text-3xl text-[#0B1B3D]">Custom</span>
                  ) : (
                    <>
                      <span className="font-heading font-extrabold text-3xl text-[#0B1B3D]">{inr(p.price_monthly)}</span>
                      <span className="text-sm text-muted-foreground">/month</span>
                    </>
                  )}
                </p>
                {!p.custom && (
                  <p className="text-xs text-muted-foreground mt-1">{inr(p.price_yearly)}/year billed annually</p>
                )}
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.slice(0, 6).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                  {p.features.length > 6 && (
                    <li className="text-xs text-muted-foreground">+ {p.features.length - 6} more</li>
                  )}
                </ul>
                <Button
                  className={`mt-8 rounded-full w-full ${
                    p.highlight
                      ? "bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-bold"
                      : "bg-[#0B1B3D] hover:bg-[#13264f]"
                  }`}
                  onClick={() => navigate("/register?role=employer_owner")}
                  data-testid={`plan-cta-${p.id}`}
                >
                  {p.custom ? "Talk to Sales" : "Start Hiring"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-border/60 py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold tracking-tight text-[#0B1B3D] text-center">
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
            {(landing?.faqs || []).map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left font-heading font-semibold" data-testid={`faq-q-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* INSIGHTS / BLOG */}
      {blogs.length > 0 && (
        <section className="py-24 lg:py-32" data-testid="blog-highlights">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-14">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-[#2563EB] mb-3">Insights</p>
                <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#0B1B3D]">From the Job Spring blog</h2>
              </div>
              <Link to="/blog" className="text-sm font-semibold text-[#2563EB] hover:underline shrink-0" data-testid="blog-view-all">
                View all →
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {blogs.slice(0, 3).map((b) => (
                <Link key={b.id} to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`}>
                  <div className="bg-white border border-border/60 rounded-2xl p-7 card-hover h-full">
                    <p className="text-[11px] text-muted-foreground">{new Date(b.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} · {b.author}</p>
                    <h3 className="font-heading font-semibold text-lg text-[#0B1B3D] mt-2">{b.title}</h3>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{b.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="navy-bg noise-overlay py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#2563EB]/25 blur-3xl rounded-full" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-heading font-extrabold tracking-tight text-white">
            Empowering Careers, Every Day.
          </h2>
          <p className="text-white/60 mt-5 max-w-xl mx-auto">
            Join the platform that takes you from job discovery to workforce growth — one intelligent ecosystem.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Button
              size="lg"
              onClick={() => navigate("/register?role=employer_owner")}
              className="rounded-full bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-bold px-8"
              data-testid="cta-start-hiring-btn"
            >
              Start Hiring
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/register?role=candidate")}
              className="rounded-full border-white/25 text-white hover:bg-white/10 hover:text-white px-8"
              data-testid="cta-find-jobs-btn"
            >
              Find Jobs
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
